import { useEffect, useState } from 'react';
import { Input, Modal, Typography } from 'antd';

export interface TextEditModalProps {
  open: boolean;
  title: string;
  /** Also the textarea's accessible name. */
  label: string;
  initialValue: string;
  maxLength?: number;
  /** Permit saving an empty string. Summaries are nullable; note text is not. */
  allowEmpty?: boolean;
  saving: boolean;
  onSave: (value: string) => void;
  onCancel: () => void;
}

/**
 * One-field text editor shared by the AI Summary and Notes surfaces.
 *
 * A modal rather than an inline textarea: note text runs long, and the Discovery
 * cell editor next to it is already a modal — one interaction language beats two.
 *
 * The parent owns the save lifecycle (`saving`, error toasts, 409 handling); this
 * component only guards against losing unsaved work.
 */
export function TextEditModal({
  open, title, label, initialValue, maxLength = 20000,
  allowEmpty = false, saving, onSave, onCancel,
}: TextEditModalProps) {
  const [value, setValue] = useState(initialValue);

  // Re-seed whenever the modal is (re)opened against a different row. Without
  // this, opening note B after editing note A shows A's draft.
  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const dirty = value !== initialValue;
  const canSave = allowEmpty || value.trim() !== '';

  // Warn on tab close while an edit is in flight, mirroring the whole-lead edit
  // mode in LeadDetailBody. The in-app discard confirm below cannot cover a
  // browser close, and note text is the kind of thing an operator retypes from
  // memory if it is lost.
  useEffect(() => {
    if (!open || !dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [open, dirty]);

  function handleCancel() {
    if (!dirty) {
      onCancel();
      return;
    }
    Modal.confirm({
      title: 'Discard changes?',
      content: 'Your edits will be lost.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: onCancel,
    });
  }

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleCancel}
      onOk={() => onSave(value)}
      okText="Save"
      okButtonProps={{ loading: saving, disabled: !canSave }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
    >
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Input.TextArea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoSize={{ minRows: 6, maxRows: 20 }}
        maxLength={maxLength}
        showCount
        aria-label={label}
        disabled={saving}
      />
    </Modal>
  );
}
